import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ScienceIcon from '@mui/icons-material/Science';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const BatchLogPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    batch_logs_overview: false,
    batch_logs_premix: false,
    batch_logs_ingredients: false,
    batch_logs_qc_checks: false,
    batch_logs_deviations: false,
    batch_logs_yield: false,
    batch_logs_signatures: false
  });
  
  // Batch logs pages mapping
  const batchLogPages = [
    'batch_logs_overview', 
    'batch_logs_premix', 
    'batch_logs_ingredients', 
    'batch_logs_qc_checks', 
    'batch_logs_deviations', 
    'batch_logs_yield', 
    'batch_logs_signatures'
  ];
  
  const pageNameMapping = {
    'batch_logs_overview': 'Overview Log Page',
    'batch_logs_premix': 'Pre-Mix Log Page',
    'batch_logs_ingredients': 'Ingredients Log Page',
    'batch_logs_qc_checks': 'QC Checks Log Page',
    'batch_logs_deviations': 'Deviations Log Page',
    'batch_logs_yield': 'Yield Log Page',
    'batch_logs_signatures': 'Signatures Page'
  };
  
  const pageIconMapping = {
    'batch_logs_overview': <DashboardIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_logs_premix': <ScienceIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_logs_ingredients': <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_logs_qc_checks': <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_logs_deviations': <WarningIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_logs_yield': <TrendingUpIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_logs_signatures': <AssignmentIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderBatchLogPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'batch_logs_overview') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.overview'));
    } else if (pageKey === 'batch_logs_premix') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.premix'));
    } else if (pageKey === 'batch_logs_ingredients') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.ingredients'));
    } else if (pageKey === 'batch_logs_qc_checks') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.qc_checks'));
    } else if (pageKey === 'batch_logs_deviations') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.deviations'));
    } else if (pageKey === 'batch_logs_yield') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.yield'));
    } else if (pageKey === 'batch_logs_signatures') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.signatures'));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit') && !p.name?.includes('close_issue') && !p.name?.includes('start_capa') && !p.name?.includes('close_investigation') && !p.name?.includes('approve') && !p.name?.includes('supervisor') && !p.name?.includes('quality_control') && !p.name?.includes('final_release'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete') && !p.name?.includes('close_issue') && !p.name?.includes('start_capa') && !p.name?.includes('close_investigation'));
    
    // Special permissions
    const approvePerm = permissions.find(p => p.name === 'production.batch_logs.qc_checks.approve');
    const closeIssuePerm = permissions.find(p => p.name === 'production.batch_logs.deviations.close_issue');
    const startCapaPerm = permissions.find(p => p.name === 'production.batch_logs.deviations.start_capa');
    const closeInvestigationPerm = permissions.find(p => p.name === 'production.batch_logs.deviations.close_investigation');
    const supervisorPerm = permissions.find(p => p.name === 'production.batch_logs.signatures.supervisor');
    const qualityControlPerm = permissions.find(p => p.name === 'production.batch_logs.signatures.quality_control');
    const finalReleasePerm = permissions.find(p => p.name === 'production.batch_logs.signatures.final_release');

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission - for overview, premix, ingredients, qc_checks (non-approve), deviations (non-special), yield */}
            {editPerm && (pageKey === 'batch_logs_overview' || pageKey === 'batch_logs_premix' || pageKey === 'batch_logs_ingredients' || pageKey === 'batch_logs_qc_checks' || pageKey === 'batch_logs_deviations' || pageKey === 'batch_logs_yield') && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: ((pageKey === 'batch_logs_ingredients' && deletePerm) || (pageKey === 'batch_logs_qc_checks' && deletePerm) || (pageKey === 'batch_logs_qc_checks' && approvePerm) || (pageKey === 'batch_logs_deviations' && (closeIssuePerm || startCapaPerm || closeInvestigationPerm))) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission - for ingredients, qc_checks */}
            {deletePerm && (pageKey === 'batch_logs_ingredients' || pageKey === 'batch_logs_qc_checks') && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'batch_logs_qc_checks' && approvePerm) || (pageKey === 'batch_logs_deviations' && (closeIssuePerm || startCapaPerm || closeInvestigationPerm)) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Approve QC Tests Permission - for qc_checks */}
            {approvePerm && pageKey === 'batch_logs_qc_checks' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'batch_logs_deviations' && (closeIssuePerm || startCapaPerm || closeInvestigationPerm)) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Approve QC Tests
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can approve quality control tests
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[approvePerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[approvePerm.id]) {
                      newPermissions[approvePerm.id] = {};
                    }
                    newPermissions[approvePerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Deviations special permissions */}
            {pageKey === 'batch_logs_deviations' && (
              <>
                {closeIssuePerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: startCapaPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Close Deviation Issue
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can close deviation issues
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[closeIssuePerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[closeIssuePerm.id]) {
                          newPermissions[closeIssuePerm.id] = {};
                        }
                        newPermissions[closeIssuePerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
                {startCapaPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: closeInvestigationPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Start CAPA Investigation
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can start CAPA investigations
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[startCapaPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[startCapaPerm.id]) {
                          newPermissions[startCapaPerm.id] = {};
                        }
                        newPermissions[startCapaPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
                {closeInvestigationPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: (pageKey === 'batch_logs_signatures' && (supervisorPerm || qualityControlPerm || finalReleasePerm)) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Close Investigation
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can close investigations
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[closeInvestigationPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[closeInvestigationPerm.id]) {
                          newPermissions[closeInvestigationPerm.id] = {};
                        }
                        newPermissions[closeInvestigationPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}

            {/* Signatures special permissions */}
            {pageKey === 'batch_logs_signatures' && (
              <>
                {editPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: (supervisorPerm || qualityControlPerm || finalReleasePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Edit
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can edit signature data
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[editPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[editPerm.id]) {
                          newPermissions[editPerm.id] = {};
                        }
                        newPermissions[editPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
                {supervisorPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: qualityControlPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Production Supervisor Signature
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can provide supervisor signature
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[supervisorPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[supervisorPerm.id]) {
                          newPermissions[supervisorPerm.id] = {};
                        }
                        newPermissions[supervisorPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
                {qualityControlPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: finalReleasePerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Quality Control Signature
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can provide quality control signature
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[qualityControlPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[qualityControlPerm.id]) {
                          newPermissions[qualityControlPerm.id] = {};
                        }
                        newPermissions[qualityControlPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
                {finalReleasePerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Final Release Signature
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can provide final release signature
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[finalReleasePerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[finalReleasePerm.id]) {
                          newPermissions[finalReleasePerm.id] = {};
                        }
                        newPermissions[finalReleasePerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {batchLogPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderBatchLogPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default BatchLogPermissions;
